import { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart3, RefreshCw } from "lucide-react";

const WINDOWS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
];

export default function FunnelView() {
  const [windowKey, setWindowKey] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = (w) => {
    setLoading(true);
    api
      .get(`/admin/funnel?window=${w}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(windowKey);
  }, [windowKey]);

  return (
    <section className="space-y-5" data-testid="funnel-view">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold">Conversion Funnel</h2>
          <p
            className="font-mono text-[10px] uppercase tracking-wider mt-1"
            style={{ color: "var(--sb-text-muted)" }}
          >
            {data?.label || "Loading…"} · drop-off between every stage
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div
            className="inline-flex rounded-lg overflow-hidden"
            style={{ border: "1px solid var(--sb-border)" }}
          >
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                data-testid={`funnel-window-${w.id}`}
                onClick={() => setWindowKey(w.id)}
                className="px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider"
                style={{
                  background: windowKey === w.id ? "var(--sb-primary)" : "transparent",
                  color: windowKey === w.id ? "#000" : "var(--sb-text-secondary)",
                  fontWeight: windowKey === w.id ? 700 : 500,
                }}
              >
                {w.label}
              </button>
            ))}
          </div>
          <button
            data-testid="funnel-refresh-btn"
            onClick={() => load(windowKey)}
            className="sb-btn-outline px-3 py-2 text-xs uppercase inline-flex items-center gap-2"
            disabled={loading}
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {!data ? (
        <p className="font-mono text-xs" style={{ color: "var(--sb-text-muted)" }}>
          Loading…
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Stat label="Visits" value={data.totals.visits} />
            <Stat label="Unique" value={data.totals.unique_visits} />
            <Stat label="Banner Imps" value={data.totals.banner_impressions} />
            <Stat label="Banner Clicks" value={data.totals.banner_clicks} accent />
            <Stat label="Bookmaker Clicks" value={data.totals.offer_clicks} accent />
          </div>

          <FunnelCard
            title="Banner Funnel"
            subtitle="Site visit → carousel impression → carousel click"
            stages={data.banner_funnel}
            accent="#3D7EFF"
            testId="banner-funnel"
          />

          <FunnelCard
            title="Bookmaker Funnel"
            subtitle="Site visit → bookmaker offer card click"
            stages={data.bookmaker_funnel}
            accent="#00E676"
            testId="bookmaker-funnel"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TopList
              title="// Top Banners (by clicks)"
              rows={data.top_banners}
              labelKey="banner_id"
              testId="top-banners"
            />
            <TopList
              title="// Top Bookmakers (by clicks)"
              rows={data.top_bookmakers}
              labelKey="bookmaker"
              testId="top-bookmakers"
            />
          </div>
        </>
      )}
    </section>
  );
}

function Stat({ label, value, accent = false }) {
  return (
    <div className="sb-card rounded-lg p-4">
      <p
        className="font-mono text-[10px] uppercase tracking-wider"
        style={{ color: "var(--sb-text-muted)" }}
      >
        {label}
      </p>
      <p
        className="font-display text-2xl font-black mt-1"
        style={{ color: accent ? "var(--sb-primary)" : "var(--sb-text-primary)" }}
      >
        {value.toLocaleString()}
      </p>
    </div>
  );
}

function FunnelCard({ title, subtitle, stages, accent, testId }) {
  const top = stages[0]?.count || 0;
  return (
    <div className="sb-card rounded-lg p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-base">{title}</h3>
          <p
            className="font-mono text-[10px] uppercase tracking-wider mt-0.5"
            style={{ color: "var(--sb-text-muted)" }}
          >
            {subtitle}
          </p>
        </div>
        <BarChart3 size={18} style={{ color: "var(--sb-text-muted)" }} />
      </div>

      <div className="space-y-3">
        {stages.map((s, i) => {
          const prev = i === 0 ? null : stages[i - 1].count;
          const widthPct = top > 0 ? Math.max(8, (s.count / top) * 100) : 8;
          const pctOfTop = top > 0 ? (s.count / top) * 100 : 0;
          const pctOfPrev = prev !== null && prev > 0 ? (s.count / prev) * 100 : null;
          const dropOff = prev !== null && prev > 0 ? 100 - pctOfPrev : null;

          return (
            <div key={s.stage} data-testid={`${testId}-stage-${i}`}>
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-mono text-[11px] uppercase tracking-wider">
                  {i + 1}. {s.stage}
                </span>
                <span className="font-mono text-xs" style={{ color: "var(--sb-text-secondary)" }}>
                  {s.count.toLocaleString()} ·{" "}
                  <span style={{ color: "var(--sb-text-primary)" }}>
                    {pctOfTop.toFixed(1)}%
                  </span>
                </span>
              </div>
              <div
                className="relative h-7 rounded-md overflow-hidden"
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid var(--sb-border)" }}
              >
                <div
                  className="absolute top-0 left-0 h-full transition-all duration-500"
                  style={{
                    width: `${widthPct}%`,
                    background: `linear-gradient(90deg, ${accent} 0%, ${accent}80 100%)`,
                    boxShadow: `inset 0 1px 0 rgba(255,255,255,0.18), 0 0 12px ${accent}40`,
                  }}
                />
                <div className="absolute inset-0 flex items-center px-3">
                  <span
                    className="font-mono text-[10px] font-bold"
                    style={{ color: widthPct > 25 ? "#0A0F14" : "var(--sb-text-primary)" }}
                  >
                    {s.count.toLocaleString()}
                  </span>
                </div>
              </div>
              {pctOfPrev !== null && (
                <div className="flex justify-end mt-1">
                  <span
                    className="font-mono text-[10px] uppercase tracking-wider"
                    style={{
                      color: dropOff > 80 ? "#FF6B61" : dropOff > 50 ? "#FFB300" : "var(--sb-text-muted)",
                    }}
                  >
                    ↓ {dropOff.toFixed(1)}% drop-off · {pctOfPrev.toFixed(1)}% pass-through
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopList({ title, rows, labelKey, testId }) {
  return (
    <div className="sb-card rounded-lg p-5" data-testid={testId}>
      <h3
        className="font-mono text-[10px] uppercase tracking-wider mb-3"
        style={{ color: "var(--sb-text-muted)" }}
      >
        {title}
      </h3>
      {rows.length === 0 ? (
        <p className="font-mono text-xs" style={{ color: "var(--sb-text-muted)" }}>
          No clicks in this window yet.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={`${r[labelKey]}-${i}`} className="flex items-center gap-3">
              <span
                className="font-mono text-[10px] w-5"
                style={{ color: "var(--sb-text-muted)" }}
              >
                #{i + 1}
              </span>
              <span className="font-mono text-xs flex-1 truncate">{r[labelKey]}</span>
              <span className="font-mono text-sm" style={{ color: "var(--sb-primary)" }}>
                {r.clicks.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
